import { isNumber } from './isNumber';
import { distanceUnits,
    distanceUnitAbbreviations,
    lengthUnits,
    depthUnits,
    heightUnits,
    lengthUnitAbbreviations,
    depthUnitAbbreviations,
    heightUnitAbbreviations,
    temperatureUnits,
    temperatureUnitAbbreviations,
    speedUnits,
    speedUnitsEnum,
    pressureUnitAbbreviations,
    pressureUnits,
    earthConstants
} from './units';

export function convertFahrenheitToCelsius(temperature, precision=0) {
    return ((temperature - 32) * 5 / 9).toFixed(precision);
}

export function convertCelsiusToFahrenheit(temperature, precision=0) {
    return (temperature * 9 / 5 + 32).toFixed(precision);
}

export function convertInchesToMillimeters(length, precision=0) {
    return (length * 25.4).toFixed(precision);
}

export function convertMillimetersToInches(length, precision=0) {
    return (length / 25.4).toFixed(precision);
}

export function convertInchesToCentimeters(measurement, precision=0) {
    return (measurement * 2.54).toFixed(precision);
}

export function convertCentimetersToInches(measurement, precision=0) {
    return (measurement * .393701).toFixed(precision);
}

export function convertFeetToMeters(measurement, precision = 0) {
    return (measurement * 0.3048).toFixed(precision);
}

export function convertMetersToFeet(measurement, precision = 0) {
    return (measurement * 3.28084).toFixed(precision);
}

export function convertMphToKph(speed, precision=0) {
    return (speed / 0.62137).toFixed(precision);
}

export const convertKilometersToMiles = (distance, precision=0, returnPlaceholder=false) => {
    if (distance==null && returnPlaceholder) {
        return '--';
    }
    return (distance * 0.621371).toFixed(precision);
};

export function convertMphToKnots(speed, precision=0) {
    return (speed * 0.868976241900648).toFixed(precision);
}

export function convertMphToMps(speed, precision=0) {
    return (speed * 0.44704).toFixed(precision);
}

export function convertMilesToKilometers(distance, precision=0) {
    return (distance * 1.60934).toFixed(precision);
}

export function convertInchesToMillibar(pressure, precision=0) {
    return (pressure * 33.8637526).toFixed(precision);
}

export function getTemperature(tempFahrenheit, unit=temperatureUnits.fahrenheit, includeAbbreviation, precision=0) {

    const tempFahrenheitToConvert = parseFloat(tempFahrenheit);

    if (isNaN(tempFahrenheitToConvert)) {
        return tempFahrenheit;
    }

    let result = tempFahrenheitToConvert.toFixed(precision);
    if (unit === temperatureUnits.celsius) {
        result = convertFahrenheitToCelsius(tempFahrenheitToConvert, precision);
    }

    return includeAbbreviation ? result + temperatureUnitAbbreviations[unit] : result;
}

export function getTemperatureFromCelsius(tempCelsius, unit=temperatureUnits.celsius, includeAbbreviation, precision=0) {

    const tempCelsiusToConvert = parseFloat(tempCelsius);

    if (isNaN(tempCelsiusToConvert)) {
        return tempCelsius;
    }

    let result = tempCelsiusToConvert.toFixed(precision);
    if (unit === temperatureUnits.fahrenheit) {
        result = convertCelsiusToFahrenheit(tempCelsiusToConvert, precision);
    }

    return includeAbbreviation ? result + temperatureUnitAbbreviations[unit] : result;
}

export function getDepth(measurement, unit = depthUnits.inches, includeAbbreviation, precision = 0) {
    const measurementToConvert = parseFloat(measurement);

    if (isNaN(measurementToConvert)) {
        return measurement;
    }

    let result = measurementToConvert.toFixed(precision);
    if (unit === depthUnits.centimeters) {
        result = convertInchesToCentimeters(measurementToConvert, precision);
    }

    return includeAbbreviation ? result + depthUnitAbbreviations[unit] : result;
}

export function getHeight(measurement, unit = heightUnits.feet, includeAbbreviation, precision = 0) {
    const measurementToConvert = parseFloat(measurement);

    if (isNaN(measurementToConvert)) {
        return measurement;
    }

    let result = measurementToConvert.toFixed(precision);
    if (unit === heightUnits.meters) {
        result = convertFeetToMeters(measurementToConvert, precision);
    }

    return includeAbbreviation ? result + heightUnitAbbreviations[unit] : result;
}

export function getLength(inches, unit=lengthUnits.inches, includeAbbreviation, precision=0) {

    const inchesToConvert = parseFloat(inches);

    if (isNaN(inchesToConvert)) {
        return inches;
    }

    let result = inchesToConvert.toFixed(precision);
    if (unit === lengthUnits.millimeters) {
        result = convertInchesToMillimeters(inchesToConvert, precision);
    }

    return includeAbbreviation ? result + lengthUnitAbbreviations[unit] : result;
}

export function getSpeed(mph, unit=speedUnits.mph, includeAbbreviation, precision=0) {

    const mphToConvert = parseFloat(mph);

    if (isNaN(mphToConvert)) {
        return mph;
    }

    let result = mphToConvert.toFixed(precision);
    if (unit === speedUnits.kph) {
        result = convertMphToKph(mphToConvert, precision);
    } else if (unit === speedUnits.knots) {
        result = convertMphToKnots(mphToConvert, precision);
    } else if (unit === speedUnits.mps) {
        result = convertMphToMps(mphToConvert, precision);
    }

    return includeAbbreviation ? result + speedUnitsEnum[unit] : result;
}

export function getDistance(miles, unit=distanceUnits.miles, includeAbbreviation, precision=0) {

    const milesToConvert = parseFloat(miles);

    if (isNaN(milesToConvert)) {
        return miles;
    }
    let result = milesToConvert.toFixed(precision);
    if (unit === distanceUnits.kilometers) {
        result = convertMilesToKilometers(milesToConvert, precision);
    }

    return includeAbbreviation ? result + distanceUnitAbbreviations[unit] : result;
}

export function getPressure(inches, unit=pressureUnits.inchesMercury, includeAbbreviation, precision=2) {

    const inchesToConvert = parseFloat(inches);

    if (isNaN(inchesToConvert)) {
        return inches;
    }
    let result = inchesToConvert.toFixed(precision);
    if (unit === pressureUnits.millibars) {
        result = convertInchesToMillibar(inchesToConvert, precision);
    }

    return includeAbbreviation ? result + pressureUnitAbbreviations[unit] : result;
}

export const getDistanceToPoint = (targetPoint, source, distanceUnit = distanceUnits.miles)  => {
    if (isNumber(targetPoint?.latitude) &&
        isNumber(targetPoint?.longitude) &&
        isNumber(source?.latitude) &&
        isNumber(source?.longitude)
        ) {

            const radius = getEquatorialRadiusForDistanceType(distanceUnit);
            const degreesLat = degreesToRadians(targetPoint.latitude - source.latitude);
            const degreesLon = degreesToRadians(targetPoint.longitude - source.longitude);
            const sourceLatRadians = degreesToRadians(source.latitude);
            const targetLatRadians = degreesToRadians(targetPoint.latitude);

            const sqOfHalfChordLengthBtnSourceAndTarget = (Math.sin(degreesLat / 2) * Math.sin(degreesLat / 2)) +
                                                          (Math.cos(sourceLatRadians) * Math.cos(targetLatRadians) *
                                                           Math.sin(degreesLon / 2) * Math.sin(degreesLon / 2));

            const angularDistanceInRads = 2 * Math.asin(Math.min(1, Math.sqrt(sqOfHalfChordLengthBtnSourceAndTarget)));
            const distanceSourceToTarget = radius * angularDistanceInRads;

            return distanceSourceToTarget;
    } else {
        return;
    }
};

export const degreesToRadians = (angleDegrees) => {
    const angleDegressAsFloat = parseFloat(angleDegrees);
    if (isNaN(angleDegressAsFloat)) {
        return
    }
    return angleDegrees * (Math.PI / 180);
};

export const getEquatorialRadiusForDistanceType = (distanceUnit) => {
    switch (distanceUnit)
    {
        case distanceUnits.miles:
            return earthConstants.equatorialRadiusInStatuteMiles;
        case distanceUnits.kilometers:
            return earthConstants.equatorialRadiusInKilometers;
        default:
            return;
    }
};
